import React, { Fragment, useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { FormattedMessageWrappedInSpan } from '../../misc';
import cloneDeep from 'lodash.clonedeep';
import classNames from 'classnames';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import CustomDialog from '../dialogs/CustomDialog';
import FormattedPrice from '../../misc/FormattedPrice';
import EmbeddedVideo from '../../misc/EmbeddedVideo';

import { SINGLE_PHOTO, PHOTO_DVD } from '../../../utils/variables';
import {
  includesVideo,
  includesSinglePhoto,
  includesPhotoDvd,
  getVideo,
  getCertificate,
  getFotoFlat,
} from '../../../utils/common';
import { trackEvent } from '../../../utils/ga-tracking';

const Pricelist = (props) => {
  const { viewer, offer, hasCertificates, onScrollToSearchForm, onShowHowToBuyHint } =
    props;
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState();
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState();
  const productsClone = cloneDeep(offer.products);
  const fotoFlat = getFotoFlat(productsClone);
  // After the FotoFlat has been removed from the list of products, the productsClone
  // list contains all further products (in case only the FotoFlat is offered, the list
  // will be empty).
  const showOtherProducts =
    includesSinglePhoto(productsClone) || includesPhotoDvd(productsClone);
  const hasVideo = includesVideo(offer.products);

  const getPreviewPhotoForCertificate = (products) => {
    return getCertificate(products).configuration.previewImageUrl;
  };

  const handleCloseVoucherDialog = () => {
    setShowVoucherDialog(false);
  };

  const handleCloseVideoDialog = () => {
    setShowVideoDialog(false);
  };

  const handleCloseCertificateDialog = () => {
    setShowCertificateDialog(false);
  };

  const shouldCenterOfffer = (fotoFlat, allProducts) => {
    if (fotoFlat && allProducts.length === 1) {
      return true;
    }
    if (!fotoFlat && allProducts.length > 0) {
      return true;
    }
    return false;
  };

  const getVideoUrl = (offer) => {
    const videoUrl = getVideo(offer).configuration?.previewVideoUrl;
    return videoUrl;
  };

  const centerOffer = shouldCenterOfffer(fotoFlat, productsClone);

  useEffect(() => {
    if (hasVideo) {
      setVideoUrl(getVideoUrl(offer.products));
    }
    if (hasCertificates) {
      setCertificatePreviewUrl(getPreviewPhotoForCertificate(offer.products));
    }
  }, [hasCertificates, hasVideo, offer.products]);

  return (
    <Fragment>
      <div className="pricelist-container">
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            <h1 className="separator">
              <FormattedMessageWrappedInSpan
                id="pricelist.title"
                defaultMessage="Offer"
              />
            </h1>
          </div>
        </div>
        <div
          className={classNames('row', {
            'center-sm': centerOffer,
          })}
        >
          {fotoFlat && (
            <div
              className={classNames('col-xs-16', 'col-sm-6', {
                'col-sm-offset-2': !centerOffer,
              })}
            >
              <div className="pricelist pricelist-primary">
                <div className="pricelist-header">
                  <FormattedMessageWrappedInSpan
                    id="global.fotoFlat"
                    defaultMessage="Foto-Flat"
                  />
                </div>
                <div className="pricelist-body">
                  <div className="pricelist-item">
                    <FormattedMessageWrappedInSpan
                      id="pricelist.personalPhotos"
                      defaultMessage="All personal digital photos (JPG)"
                    />
                  </div>
                  <div className="pricelist-item">
                    <FormattedMessageWrappedInSpan
                      id="pricelist.impressionPhotos"
                      defaultMessage="All impressions (JPG)"
                    />
                  </div>
                  {hasVideo && (
                    <div
                      className={classNames('pricelist-item pricelist-item-video', {
                        'pricelist-item-voucher_actionable': Boolean(videoUrl),
                      })}
                      onClick={() => {
                        if (videoUrl) {
                          setShowVideoDialog(true);
                        }
                      }}
                    >
                      <FormattedMessageWrappedInSpan
                        id="fotoFlatOffer.videoOffert"
                        defaultMessage="Personalized video (MP4)"
                      />
                      {videoUrl && <span className="icon eye" />}
                    </div>
                  )}
                  {hasCertificates && (
                    <div
                      className="pricelist-item pricelist-item_actionable"
                      onClick={() => {
                        setShowCertificateDialog(true);
                      }}
                    >
                      <FormattedMessageWrappedInSpan
                        id="fotoFlatOffer.certificatesOffert"
                        defaultMessage="Printable certificate (JPEG)"
                      />
                      <span className="icon eye" />
                    </div>
                  )}
                  {offer.voucherCampaigns.map((voucherCampaign) => {
                    return (
                      <div
                        key={voucherCampaign.id}
                        className="pricelist-item pricelist-item-voucher pricelist-item_actionable"
                        onClick={() => {
                          setShowVoucherDialog({ voucherCampaign: voucherCampaign });
                          trackEvent('Event', 'Voucher', 'Offer pricelist');
                        }}
                      >
                        <div
                          className="pricelist-item-voucher-text"
                          dangerouslySetInnerHTML={{
                            __html: voucherCampaign.shortText,
                          }}
                        />
                        <span className="icon eye" />
                      </div>
                    );
                  })}
                </div>
                <div
                  className="pricelist-main-price"
                  onClick={() => {
                    onScrollToSearchForm();
                    onShowHowToBuyHint(true);
                  }}
                >
                  <FormattedPrice price={fotoFlat.price} />
                </div>
              </div>
            </div>
          )}
          {showOtherProducts && (
            <div className="col-sm-6 col-xs-16">
              <div className="pricelist">
                <div className="pricelist-header">
                  <FormattedMessageWrappedInSpan
                    id="pricelist.basicOfferTitle"
                    defaultMessage="Basic"
                  />
                </div>
                <div className="pricelist-body">
                  {productsClone.map((p) => {
                    if (p.type === SINGLE_PHOTO && !p.mediaCollection) {
                      return (
                        <div key={p.id} className="pricelist-item">
                          <FormattedMessageWrappedInSpan
                            id="pricelist.singlePhoto"
                            defaultMessage="1 personal digital photo (JPG)"
                          />
                          <span>
                            <FormattedPrice price={p.price} />
                          </span>
                        </div>
                      );
                    }
                    if (p.type === PHOTO_DVD) {
                      return (
                        <div key={p.id} className="pricelist-item">
                          <FormattedMessageWrappedInSpan
                            id="pricelist.additionalShipment"
                            defaultMessage="Optional: shipping of your order on DVD"
                          />
                          <span>
                            <FormattedPrice price={p.price} />
                          </span>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {offer.voucherCampaigns.map((voucherCampaign) => {
        return (
          <CustomDialog
            hasFixedWidth={false}
            open={
              showVoucherDialog &&
              showVoucherDialog.voucherCampaign.id == voucherCampaign.id
            }
            onRequestClose={handleCloseVoucherDialog}
          >
            <div className="notification-container">
              <DialogContent>
                <div className="notification-body">
                  <div dangerouslySetInnerHTML={{ __html: voucherCampaign.longText }} />
                  <div className="voucher-dialog-image">
                    <a
                      href={voucherCampaign.url}
                      onClick={() => trackEvent('Event', 'Voucher', 'Banner dialog')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={voucherCampaign.banner} />
                    </a>
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <div className="notification-action">
                  <button className="accept" onClick={handleCloseVoucherDialog}>
                    <FormattedMessageWrappedInSpan
                      id="pricelist.dialog.acceptButton"
                      defaultMessage="Ok"
                    />
                  </button>
                </div>
              </DialogActions>
            </div>
          </CustomDialog>
        );
      })}
      {hasVideo && videoUrl && (
        <CustomDialog
          hasFixedWidth={false}
          open={showVideoDialog}
          onRequestClose={handleCloseVideoDialog}
        >
          <div className="notification-container notification-video">
            <DialogContent>
              <div className="notification-body ">
                <h1>
                  <FormattedMessageWrappedInSpan
                    id="pricelist.dialog.video.title"
                    defaultMessage="Video preview"
                  />
                </h1>
                <EmbeddedVideo videoUrl={videoUrl} />
              </div>
            </DialogContent>
            <DialogActions>
              <div className="notification-action">
                <button className="accept" onClick={handleCloseVideoDialog}>
                  <FormattedMessageWrappedInSpan
                    id="pricelist.dialog.acceptButton"
                    defaultMessage="Ok"
                  />
                </button>
              </div>
            </DialogActions>
          </div>
        </CustomDialog>
      )}
      {hasCertificates && (
        <CustomDialog
          hasFixedWidth={false}
          open={showCertificateDialog}
          onRequestClose={handleCloseCertificateDialog}
        >
          <div className="notification-container ">
            <DialogContent>
              <div className="notification-body certificate-body">
                <h1>
                  <FormattedMessageWrappedInSpan
                    id="pricelist.dialog.certificate.title"
                    defaultMessage="What is certificate?"
                  />
                </h1>
                {certificatePreviewUrl && (
                  <div className="certificate-dialog-image">
                    <img src={certificatePreviewUrl} alt="certificate preview" />
                  </div>
                )}
                <div className="pricelist-item-certificate-text">
                  <FormattedMessageWrappedInSpan
                    id="pricelist.dialog.certificate"
                    defaultMessage="You can create a certificate with a photo of your choice from the pictures of your Foto-Flat and with your personal data. You can download and print it."
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <div className="notification-action">
                <button className="accept" onClick={handleCloseCertificateDialog}>
                  <FormattedMessageWrappedInSpan
                    id="pricelist.dialog.acceptButton"
                    defaultMessage="Ok"
                  />
                </button>
              </div>
            </DialogActions>
          </div>
        </CustomDialog>
      )}
    </Fragment>
  );
};

Pricelist.propTypes = {
  offer: PropTypes.object,
  hasCertificates: PropTypes.bool,
  onScrollToSearchForm: PropTypes.func,
  onShowHowToBuyHint: PropTypes.func,
};

export { Pricelist };

export default createFragmentContainer(Pricelist, {
  offer: graphql`
    fragment Pricelist_offer on Event {
      products {
        id
        mediaCollection {
          name
        }
        price
        type
        configuration {
          ... on SingleVideoConfiguration {
            previewVideoUrl
          }
          ... on EventCertificateConfiguration {
            previewImageUrl
          }
        }
      }
      voucherCampaigns {
        id
        banner
        longText
        name
        shortText
        url
      }
    }
  `,
});
